import React, { useState, useEffect, Suspense } from "react";
import { Collapse, Form, Row, Col, Tooltip, Icon } from "antd";
import { SelectionProcessesService } from "@frontend/src/core/services/selection_processes_service";
import { openNotificationWithIcon } from "@frontend/src/utilities/notificationWithIcon";
import { Show } from '@frontend/components/atoms/show';
import { KudertProfilesList } from "@rankmi/ats-app";
import ExperienceEducationForm from "../experience_education/ExperienceEducationForm";
import FilesForm from "../files/FilesForm";
import "./MainForm.scss";
import QuestionsForm from "../questions/QuestionsForm";
import LanguageForm from "../language/LanguageForm";
import SkillForm from "../skills/SkillsForm";
import AssessmentsMettlForm from "../assessments-mettl-form/AssessmentsMettlForm";
import TruoraSettingsForm from "../truora-settings-form/TruoraSettingsForm";

const MainFormAux = ({
  selection_process,
  languages,
  form,
  tests,
  enterpriseTruoraActivation,
  testsAvailable,
  readonly,
}) => {
  const service = new SelectionProcessesService();
  const [data, setData] = useState(null);

  useEffect(() => {
    service.getById(selection_process.id, setData, console.error);
  }, []);

  const redirect = (data) => {
    window.location.href = `filter_form`;
  };

  const question_title = (
    <Tooltip
      title="Para el uso de integraciones, solo las preguntas Abiertas son publicadas en los portales externos con restricción de un máx de preguntas según las políticas de cada portal"
      placement="bottom"
    >
      Preguntas
      <Icon className="comment-label-tooltip" type="info-circle" />
    </Tooltip>
  );

  const onSubmit = (e, form) => {
    e.preventDefault();
    form.validateFields((err, values) => {
      let error = '';
      const request = { ...values };
      if (values.language_attributes.length > 0) {
        const lang = values.language_attributes.map((value) => ({
          language_id: values.language[value],
        }));
        request.language_with_levels_attributes = lang;
      }

      if (values.skill_attributes.length > 0) {
        const skill = values.skill_attributes.map((value) => ({
          skill_id: values.skill[value],
        }));
        request.skill_with_levels_attributes = skill;
      }

      if (values.question_attributes.length > 0) {
        const questions = [];

        values.question_attributes.forEach((value, index) => {
          const options = [];
          const res = {};
          res.id = values.question_id[value];
          res.input_type = values.input_type[value];
          res.question = values.question[value];
          res.required = values.question_required[value];

          if (res.input_type === 'list') {
            if (values[`options${value}`].length > 1) {
              values[`option${value}`].forEach((elem, index) => {
                options.push({
                  id: values[`option_id${value}`][index] || null,
                  value: elem,
                });
              });
            } else {
              error =
                'Una pregunta de selección no puede tener menos de 2 opciones';
            }
          } else {
            res.is_video = values.question_is_video[value];
          }
          res.selection_process_options_attributes = options;
          questions.push(res);
        });

        request.selection_process_questions_attributes = questions;
      }
      Number(values.age_minimum)
      ? request.age_minimum = Number(values.age_minimum)
      : request.age_minimum = null

      if (!err && error === '') {
        service.saveQuestions(
          selection_process.id,
          { selection_process: request },
          redirect,
          console.error
        );
      } else {
        openNotificationWithIcon('warning', 'Completa la información', error);
      }
    });
  };

  const initValues = {
    education_required: data?.education_required,
    experience_required: data?.experience_required,
    age_options: data?.age_options,
    gender_options: data?.gender_options,
    ethnicity_options: data?.ethnicity_options,
    nationality_options: data?.nationality_options,
    residence_options: data?.residence_options,
    applicant_autorization_options: data?.applicant_autorization_options,
    hidden_curriculum: data?.hidden_curriculum,
    number_required_files: data?.number_required_files,
    required_rut: data?.required_rut,
    age_minimum: data?.age_minimum,
    rfc_options: data?.rfc_options,
    nss_options: data?.nss_options,
    civil_status_options: data?.civil_status_options,
    validate_document: data?.validate_document,
    postal_code_options: data?.postal_code_options,
  };

  return (
    <>
      {data && (
        <div className="main-form">
          <Form onSubmit={(e) => onSubmit(e, form)}>
            <Collapse>
              <Collapse.Panel key="1" header="Datos del postulante" forceRender>
                <ExperienceEducationForm
                  form={form}
                  initialValues={initValues}
                  readonly={readonly}
                />
              </Collapse.Panel>
              <Collapse.Panel key="2" header="Archivos" forceRender>
                <FilesForm
                  form={form}
                  initialValues={initValues}
                  readonly={readonly}
                />
              </Collapse.Panel>

              <Collapse.Panel key="3" header={question_title} forceRender>
                <QuestionsForm
                  initValues={{
                    questions: data.selection_process_questions,
                    income: data.income_expectation_active,
                    experience: data.experience_active,
                    disability: data.disability_active,
                  }}
                  form={form}
                  readonly={readonly}
                />
              </Collapse.Panel>
              <Collapse.Panel key="4" header="Idioma" forceRender>
                <LanguageForm
                  form={form}
                  initValues={data.languages}
                  languages={languages}
                  readonly={readonly}
                />
              </Collapse.Panel>
              <Collapse.Panel key="5" header="Habilidades" forceRender>
                <SkillForm
                  form={form}
                  initValues={data.skills}
                  readonly={readonly}
                />
              </Collapse.Panel>
              {enterpriseTruoraActivation && (
                <Collapse.Panel
                  key="6"
                  header="Validación de Antecedentes"
                  forceRender
                >
                  <TruoraSettingsForm
                    truoraActivation={data.truora_activation}
                    stock={data.truora_stock}
                    form={form}
                  />
                </Collapse.Panel>
              )}
              <Collapse.Panel key="7" header="Pruebas" forceRender>
                <AssessmentsMettlForm
                  processId={selection_process.id}
                  form={form}
                  activated={data.test_activation}
                />
              </Collapse.Panel>
              {testsAvailable.kudert && (
                <Collapse.Panel key="8" header="Perfiles de Kudert" forceRender>
                  <Suspense fallback="">
                    <KudertProfilesList
                      selectionProcessId={selection_process.id}
                      selectedIds={selection_process.kudert_profile_ids}
                    />
                  </Suspense>
                </Collapse.Panel>
              )}
            </Collapse>
            <Row type="flex" justify="end">
              <Col xs={{ span: 2 }} style={{ marginRight: 10 }}>
                <a
                  className="btn btn-outline-user"
                  onClick={() => {
                    window.location.href = `edit`;
                  }}
                >
                  Atrás
                </a>
              </Col>
              <Col xs={{ span: 5 }}>
                <button type="submit" className=" btn btn-sair">
                  Guardar y continuar
                </button>
              </Col>
            </Row>
          </Form>
        </div>
      )}
    </>
  );
};

const MainForm = Form.create({ name: 'main_form' })(MainFormAux);

export default MainForm;
